import React from 'react';
import { makeStyles, Theme } from '@material-ui/core';

import { Dictionary } from 'lodash';
import { groupBy } from 'lodash';
import { ChartTimeSeries } from './types';
import { Chip } from '../../ui/Chip';
import { dividerColor } from '../../../data/colors';
import { Card } from '../../ui/card';
import { SecondaryButton } from '../../ui/Buttons';
import { splitCamelCase, replacePercent } from 'src/helpers/strings';

interface LegendProps {
  snapshots: Dictionary<ChartTimeSeries>;
  nearest?: Dictionary<number | undefined>;
  setHighlighted: (tag: string | undefined) => void;
  toggleActive: (tags: string) => void;
  active: string[];
  removeTagFromChartGroup: (tagId: string) => void;
  editPointsHandler: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: '8px',
    gap: '8px',
  },
  legendItem: {
    borderRadius: '50%',
    width: 24,
    height: 24,
    minHeight: 24,
    minWidth: 24,
  },
  inactive: {
    backgroundColor: 'transparent !important',
    border: `2px solid ${dividerColor}`,
  },
  chip: {
    marginRight: '8px',
    width: '100%',
    justifyContent: 'start',
    textAlign: 'left',
    '& .MuiChip-label': {
      width: '100%',
    },
  },
  chipsAndButton: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      gap: 8,
      flexDirection: 'column',
    },
  },
  chipContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    paddingRight: 8,
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: '1fr 1fr 1fr',
    },

    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      paddingRight: 0,
    },

    width: '100%',
    gap: 4,
  },
  buttonContainer: {
    textWrapMode: 'nowrap',
  },
}));

export const Legend: React.FC<LegendProps> = (props) => {
  const {
    snapshots,
    setHighlighted,
    toggleActive,
    active,
    removeTagFromChartGroup,
    editPointsHandler,
    nearest,
  } = props;
  const classes = useStyles();
  const unitGroups = groupBy(Object.values(snapshots), (s) => s.tag.unit);

  return (
    <Card className={classes.legend}>
      <div className={classes.chipsAndButton}>
        <div className={classes.chipContainer}>
          {Object.keys(unitGroups).map((unit) => {
            return unitGroups[unit].map((s, j) => {
              const label = splitCamelCase(s.tag.label || s.tag.id);
              const isActive = active.includes(s.tagId);
              const value =
                nearest && nearest[s.tag.id] !== undefined
                  ? (nearest[s.tag.id] as number).toFixed(2)
                  : '';

              return (
                <div key={label}>
                  <Chip
                    key={j}
                    className={classes.chip}
                    avatar={
                      <div
                        className={`${isActive ? s.legendClass : classes.inactive} ${
                          classes.legendItem
                        } `}
                      />
                    }
                    label={`${label} ${value} (${replacePercent(unit)})`}
                    color={'default'}
                    onClick={() => toggleActive(s.tagId)}
                    onDelete={() => removeTagFromChartGroup(s.tagId)}
                    onMouseEnter={() => setHighlighted(s.tagId)}
                    onMouseLeave={() => setHighlighted(undefined)}
                  />
                </div>
              );
            });
          })}
        </div>
        <div className={classes.buttonContainer}>
          <SecondaryButton onClick={editPointsHandler}>Edit Points</SecondaryButton>
        </div>
      </div>
    </Card>
  );
};
